import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { LOGIN_URL } from '../lib/auth-client.js';
import { Note, Problem, button } from '../components/form.js';
import {
  hasProviderToAdd,
  providerLabel,
  unlinkProvider,
  type SignInMethods as Methods,
} from '../lib/sign-in-methods.js';

/**
 * Every way into this account, listed — and removable.
 *
 * ── WHY IT WAS NEEDED ────────────────────────────────────────────────
 *
 * Jon asked for it on 2026-08-29, from the case that makes it obvious: one
 * address can carry a Google account, a Microsoft account and an Apple ID, and
 * signing in with each of them links all three to the same Ridgewill account.
 * Nothing in the portal said so. The account had three doors and named none of
 * them, while listing passkeys and devices in detail directly below.
 *
 * It is also the section that makes the Password card above make sense. "You do
 * not have a password" is alarming on its own and unremarkable once the line
 * above it says you sign in with Google.
 *
 * ── IT REMOVES HERE, AND ADDS SOMEWHERE ELSE ─────────────────────────
 *
 * This section shipped read-only, with a comment saying `/link-social` and
 * `/unlink-account` were both absent from `ACCOUNT_SERVES` because linking
 * starts an OAuth redirect and this deployment refuses sign-in flows (ADR
 * 0002). That reason is sound and it only ever applied to LINKING (auth#315).
 * Unlinking starts no redirect: it is a session-gated delete of one of the
 * caller's own rows, and it now sits on that allow-list.
 *
 * `/link-social` is still not on it and should not be, so adding a provider
 * links out to the hosted login's `/connect` screen — the same shape the
 * Password card uses, and for the same reason: the session cookie is scoped to
 * the parent domain, so it crosses the origin and there is nothing to hand
 * over.
 *
 * ── THE NOTE HERE USED TO BE TRUE AND STOPPED BEING ──────────────────
 *
 * It said "adding a provider happens when you sign in with it, not from this
 * page", which described implicit linking exactly: signing in with a provider
 * this account had never used attached it on a matching email address.
 * RidgeWill/auth#331 turned that off, so the sentence became instructions for
 * something that now fails with `account_not_linked`. A note explaining a
 * mechanism that has been removed is worse than no note, because it reads as
 * current.
 *
 * ── EVERY PROVIDER IS REMOVABLE, INCLUDING THE LAST ──────────────────
 *
 * There was a rule here that withheld the control from the last remaining
 * `account` row, because better-auth refused it, plus a paragraph explaining
 * why. Two versions of that paragraph were written and both were false in the
 * same direction: they asserted a lockout that does not exist.
 *
 * The premise was wrong, not just the wording, and it cost a real case:
 * somebody with one provider AND a passkey could not remove the provider,
 * because a passkey is not an `account` row. `allowUnlinkingAll` is now on in
 * the auth service (auth#324), so the button is drawn for every provider.
 *
 * ── AND THE REASON IT IS SAFE HAS SINCE MOVED ────────────────────────
 *
 * This paragraph used to justify the removal by saying an account with no
 * `account` rows is still signed into, because the social callback finds the
 * user with `findUserByEmail` and links on the way through. RidgeWill/auth#331
 * turned that off. The removal is still right; what stops a lockout now is the
 * password reset, which CREATES a credential row for an account that has none
 * — pinned over there by `account-recovery.integration.test.ts`, because it
 * became load-bearing rather than a convenience.
 *
 * Which is the whole point of the change: the way back into an account is the
 * mailbox, not whichever provider happens to assert the address.
 *
 * `unlinkProvider` still translates `FAILED_TO_UNLINK_LAST_ACCOUNT` even
 * though it should no longer be reachable. That is deliberate: this app
 * deploys independently of the service, so it can be running against a server
 * where the flag has not landed, and the honest failure there is a sentence
 * rather than a raw code.
 *
 * ── WHY REMOVAL MATTERS MORE THAN IT LOOKS ───────────────────────────
 *
 * Revoking at the provider does NOT remove our link. Disconnect Ridgewill in
 * Google's own settings and our row survives untouched; the next "Sign in with
 * Google" re-consents straight back into the same account. Until this control
 * existed there was no way, from our side OR the provider's, to make a linked
 * provider stop being a door.
 *
 * The case that makes it concrete: somebody signs up with a work Microsoft
 * identity and later leaves the company. IT reopens that mailbox. Whoever
 * controls it can sign in as them indefinitely.
 *
 * ── AN EMPTY LIST IS NOT THE SAME AS AN UNANSWERED ONE ───────────────
 *
 * `null` means the lookup failed and the section says so; `undefined` means it
 * has not answered yet and says something quieter. Rendering an empty list for
 * either would state that no provider is connected, which is a claim about
 * somebody's account made from no evidence — and the reader has no way to tell
 * the difference.
 */
export function SignInMethods({
  methods,
  available,
  onChanged,
}: {
  methods: Methods | null | undefined;
  /** What this deployment could offer. Null when it could not be asked — see
   *  `hasProviderToAdd` for why that keeps the control rather than hiding it. */
  available: string[] | null;
  /** Re-reads the list. The Password card below reads the same answer, so a
   *  stale list after a removal is two cards disagreeing. */
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Outstanding, which is not the same as failed. Both leave the list unknown
  // and only one of them is worth an alarming red box.
  if (methods === undefined) {
    return <p style={{ margin: 0, color: C.muted }}>Checking&hellip;</p>;
  }

  if (methods === null) {
    return (
      <Problem>
        We could not load your sign-in methods just now. Nothing has changed, so try again in a
        moment.
      </Problem>
    );
  }

  async function remove(accountId: string, label: string) {
    const confirmed = window.confirm(
      'Remove ' + label + '? You will not be able to sign in with it until you connect it again.',
    );
    if (!confirmed) return;

    setBusy(accountId);
    setError(null);
    const message = await unlinkProvider(accountId);
    setBusy(null);
    if (message) {
      setError(message);
      return;
    }
    onChanged();
  }

  /**
   * PROVIDERS ONLY. The password row was here and has gone (Jon, 2026-09-01).
   *
   * The section is named for single sign-on now, and a row reading "Email and
   * password" inside a card titled "Single Sign-On Providers" is a heading
   * arguing with its contents. The Password card directly below already says
   * whether there is one, by offering "Change" or "Set".
   *
   * What the old arrangement bought was that "you do not have a password" read
   * less alarmingly directly under a line saying you sign in with Google. The
   * two cards are still adjacent and still in that order, so the reading is
   * unchanged; only the duplication has gone.
   */
  const rows = methods.providers.map((p) => ({
    key: p.accountId,
    label: providerLabel(p.providerId),
    detail: 'Sign in with your ' + providerLabel(p.providerId) + ' account.',
  }));

  return (
    <>
      <Note>
        These all reach the same account. Signing in with a provider that is not listed here will
        not connect it, so add it deliberately before you rely on it.
      </Note>

      {error ? <Problem>{error}</Problem> : null}

      {rows.length === 0 ? (
        /**
         * The state EVERY account on this service is currently in, so it is the
         * common case rather than an edge one. It says what is true of
         * providers and nothing about passwords or passkeys, which the two
         * cards below own.
         */
        <p style={{ marginTop: '1rem', lineHeight: 1.5, color: C.textMid }}>
          No single sign-on providers are connected to this account.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '1rem 0 0' }}>
          {rows.map((row) => (
            <li
              key={row.key}
              style={{
                display: 'flex',
                gap: '.75rem',
                alignItems: 'baseline',
                padding: '.6rem 0',
                borderTop: `1px solid ${C.inputBorder}`,
              }}
            >
              <span style={{ fontWeight: 600, minWidth: '9rem' }}>{row.label}</span>
              <span style={{ color: C.textMid, fontSize: '.9rem', lineHeight: 1.4, flex: 1 }}>
                {row.detail}
              </span>
              <button
                type="button"
                style={button}
                disabled={busy === row.key}
                onClick={() => void remove(row.key, row.label)}
              >
                {busy === row.key ? 'Removing…' : 'Remove'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/*
        ADDING HAPPENS ON THE SIGN-IN SITE, NOT HERE (RidgeWill/auth#331).

        A plain anchor to another ORIGIN, not a fetch: `/link-social` starts an
        OAuth redirect, which is a sign-in flow, and ADR 0002 keeps those off
        this deployment. The route is absent from `ACCOUNT_SERVES` deliberately
        and a button here would 404 against the allow-list.

        `returnTo=account` brings them back afterwards, as a SLUG resolved
        server-side from TRUSTED_ORIGINS rather than an origin written into this
        file.

        HIDDEN once every available provider is connected (Jon, 2026-09-01) —
        a control whose only destination is a screen saying "nothing to add" is
        worse than no control. `hasProviderToAdd` keeps it when the available
        list could not be fetched, because withdrawing the only way to add one
        on the strength of a network error is the wrong direction to fail in.
      */}
      {hasProviderToAdd(available, methods.providers.map((p) => p.providerId)) ? (
        <p style={{ marginTop: '1.25rem' }}>
          <a
            href={`${LOGIN_URL}/connect?returnTo=account`}
            style={{ ...button, display: 'inline-block', textDecoration: 'none' }}
          >
            Add provider
          </a>
        </p>
      ) : null}
    </>
  );
}

